import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useRussiaHands,
  useCreateRussiaHand,
  useUpdateRussiaHand,
  useDeleteRussiaHand,
  RussiaHand,
  CreateRussiaHandRequest,
  UpdateRussiaHandRequest,
  PaymentMethod,
} from "../api/wallet";
import { useSearchUsersQuery } from "../api";
import { useUserFullInfo } from "../api/users";
import { useNotification } from "../hooks/useNotification";
import Notification from "../components/ui/Notification";

interface RussiaHandModalProps {
  isOpen: boolean;
  onClose: () => void;
  handler?: RussiaHand;
  mode: "create" | "edit";
}

const RussiaHandModal: React.FC<RussiaHandModalProps> = ({
  isOpen,
  onClose,
  handler,
  mode,
}) => {
  const { t } = useTranslation("dashboard");
  const { showError, showSuccess, notification, hideNotification } =
    useNotification();

  const [formData, setFormData] = useState<CreateRussiaHandRequest>({
    user_id: 0,
    who: "",
    description: "",
    receiver_tel: "",
    telegram_username: "",
    working_hours: "",
    payment_methods: [],
    sum: 0,
    sum_tether: 0,
    sum_total: 0,
    status: true,
  });

  const createMutation = useCreateRussiaHand();
  const updateMutation = useUpdateRussiaHand();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  const {
    data: searchResults,
    isFetching: isSearching,
  } = useSearchUsersQuery(
    { search: debouncedTerm, limit: 10 },
    { skip: debouncedTerm.trim().length <= 1 }
  );
  const { data: selectedUserFull } = useUserFullInfo(
    formData.user_id ? formData.user_id : null
  );

  React.useEffect(() => {
    if (handler && mode === "edit") {
      setFormData({
        user_id: handler.alny_russia_hands_user_id,
        who: handler.who,
        description: handler.description || "",
        receiver_tel: handler.receiver_tel || "",
        telegram_username: handler.telegram_username || "",
        working_hours: handler.working_hours || "",
        payment_methods: handler.payment_methods || [],
        sum: handler.alny_russia_hands_sum,
        sum_tether: handler.alny_russia_hands_sum_tether,
        sum_total: handler.alny_russia_hands_sum_total,
        status: handler.status,
      });
    } else {
      setFormData({
        user_id: 0,
        who: "",
        description: "",
        receiver_tel: "",
        telegram_username: "",
        working_hours: "",
        payment_methods: [],
        sum: 0,
        sum_tether: 0,
        sum_total: 0,
        status: true,
      });
      setSearchTerm("");
      setShowSuggestions(false);
    }
  }, [handler, mode, isOpen]);

  // Автоподстановка телефона/телеграма из полного профиля
  React.useEffect(() => {
    if (selectedUserFull) {
      setFormData((prev) => ({
        ...prev,
        receiver_tel: selectedUserFull.mobile || prev.receiver_tel,
        telegram_username:
          selectedUserFull.telegram_username || prev.telegram_username,
      }));
    }
  }, [selectedUserFull]);

  // Дебаунс поисковой строки
  React.useEffect(() => {
    const timeout = setTimeout(() => {
      const trimmed = searchTerm.trim();
      setDebouncedTerm(trimmed.length > 1 ? trimmed : "");
      setShowSuggestions(trimmed.length > 1);
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.who.trim()) {
      showError(t("russia_hands.errors.who_required"));
      return;
    }

    if (formData.sum < 0 || formData.sum_tether < 0 || formData.sum_total < 0) {
      showError(t("russia_hands.errors.negative_amount"));
      return;
    }

    if (mode === "create") {
      createMutation.mutate(formData, {
        onSuccess: (response: { message?: string }) => {
          showSuccess(response.message || t("russia_hands.success.created"));
          onClose();
        },
        onError: (error: any) => {
          const status = error?.response?.status;
          const responseData = error?.response?.data;

          let errorMessage = "";
          if (responseData) {
            if (responseData.message) {
              errorMessage = responseData.message;
            } else if (responseData.error) {
              errorMessage = responseData.error;
            }
          }

          if (!errorMessage) {
            errorMessage =
              error?.message || t("russia_hands.errors.creation_error");
          }

          let userMessage = t("russia_hands.errors.creation_error");

          switch (status) {
            case 400:
              userMessage = errorMessage;
              break;
            case 401:
              userMessage = t("russia_hands.errors.unauthorized");
              break;
            case 403:
              userMessage = t("russia_hands.errors.forbidden");
              break;
            case 422:
              userMessage = errorMessage;
              break;
            case 500:
              userMessage = t("russia_hands.errors.server_error");
              break;
            default:
              userMessage = errorMessage;
          }

          showError(userMessage);
        },
      });
    } else if (handler) {
      const updateData: UpdateRussiaHandRequest = {
        user_id: formData.user_id,
        who: formData.who,
        description: formData.description,
        receiver_tel: formData.receiver_tel,
        telegram_username: formData.telegram_username,
        working_hours: formData.working_hours,
        payment_methods: formData.payment_methods,
        sum: formData.sum,
        sum_tether: formData.sum_tether,
        sum_total: formData.sum_total,
        status: formData.status,
      };

      updateMutation.mutate(
        { id: handler.alny_russia_hands_id, data: updateData },
        {
          onSuccess: (response: { message?: string }) => {
            showSuccess(response.message || t("russia_hands.success.updated"));
            onClose();
          },
          onError: (error: any) => {
            const status = error?.response?.status;
            const responseData = error?.response?.data;

            let errorMessage = "";
            if (responseData) {
              if (responseData.message) {
                errorMessage = responseData.message;
              } else if (responseData.error) {
                errorMessage = responseData.error;
              }
            }

            if (!errorMessage) {
              errorMessage =
                error?.message || t("russia_hands.errors.update_error");
            }

            let userMessage = t("russia_hands.errors.update_error");

            switch (status) {
              case 400:
                userMessage = errorMessage;
                break;
              case 401:
                userMessage = t("russia_hands.errors.unauthorized");
                break;
              case 403:
                userMessage = t("russia_hands.errors.forbidden");
                break;
              case 404:
                userMessage = t("russia_hands.errors.not_found");
                break;
              case 422:
                userMessage = errorMessage;
                break;
              case 500:
                userMessage = t("russia_hands.errors.server_error");
                break;
              default:
                userMessage = errorMessage;
            }

            showError(userMessage);
          },
        }
      );
    }
  };

  const addPaymentMethod = () => {
    setFormData({
      ...formData,
      payment_methods: [
        ...(formData.payment_methods || []),
        { bank: "", card_number: "", type: "", comment: "" },
      ],
    });
  };

  const updatePaymentMethod = (
    index: number,
    field: keyof PaymentMethod,
    value: string
  ) => {
    const updated = [...(formData.payment_methods || [])];
    if (!updated[index]) {
      return;
    }
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, payment_methods: updated });
  };

  const removePaymentMethod = (index: number) => {
    const updated = [...(formData.payment_methods || [])];
    updated.splice(index, 1);
    setFormData({ ...formData, payment_methods: updated });
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {mode === "create"
                ? t("russia_hands.modal.create_title")
                : t("russia_hands.modal.edit_title")}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  ID пользователя / поиск
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.user_id ? formData.user_id : ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      const numeric = parseInt(value, 10);
                      setFormData({
                        ...formData,
                        user_id: isNaN(numeric) ? 0 : numeric,
                      });
                      setSearchTerm(value);
                    }}
                    onFocus={() => {
                      if (searchTerm.trim().length > 1) setShowSuggestions(true);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Начните вводить имя, email или ID"
                  />
                  {showSuggestions && (
                    <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded shadow max-h-60 overflow-y-auto">
                      {isSearching && (
                        <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                          Поиск...
                        </div>
                      )}
                      {!isSearching &&
                        (searchResults?.data?.length ?? 0) === 0 && (
                          <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                            Ничего не найдено
                          </div>
                        )}
                      {searchResults?.data?.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              user_id: u.id,
                              receiver_tel: u.mobile || formData.receiver_tel,
                            });
                            setSearchTerm(u.id.toString());
                            setShowSuggestions(false);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-900 dark:text-white"
                        >
                          <div className="font-medium">
                            #{u.id} {u.name} {u.surname}
                          </div>
                          <div className="text-xs text-gray-500">
                            {u.email}
                            {u.mobile ? ` · ${u.mobile}` : ""}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  0 для компании. При выборе пользователя телефон и Telegram
                  подставятся автоматически, если есть.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t("russia_hands.form.who")} *
                </label>
                <input
                  type="text"
                  value={formData.who}
                  onChange={(e) =>
                    setFormData({ ...formData, who: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t("russia_hands.form.who_placeholder")}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t("russia_hands.form.description")}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t("russia_hands.form.description_placeholder")}
                  rows={3}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t("russia_hands.form.description_help")}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t("russia_hands.form.telegram_username")}
                </label>
                <input
                  type="text"
                  value={formData.telegram_username}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      telegram_username: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t("russia_hands.form.telegram_username_placeholder")}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t("russia_hands.form.working_hours")}
                </label>
                <input
                  type="text"
                  value={formData.working_hours}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      working_hours: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t("russia_hands.form.working_hours_placeholder")}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t("russia_hands.form.receiver_tel")}
                </label>
                <input
                  type="tel"
                  value={formData.receiver_tel}
                  onChange={(e) =>
                    setFormData({ ...formData, receiver_tel: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t("russia_hands.form.receiver_tel_placeholder")}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t("russia_hands.form.receiver_tel_help")}
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t("russia_hands.form.payment_methods")}
                  </label>
                  <button
                    type="button"
                    onClick={addPaymentMethod}
                    className="text-blue-600 text-sm hover:underline"
                  >
                    {t("russia_hands.form.add_payment_method")}
                  </button>
                </div>
                {(formData.payment_methods || []).length === 0 ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t("russia_hands.form.payment_methods_empty")}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {(formData.payment_methods || []).map((method, index) => (
                      <div
                        key={index}
                        className="p-3 border border-gray-200 dark:border-gray-700 rounded-md space-y-2"
                      >
                        <div className="grid grid-cols-2 gap-3">
                          <input
                            type="text"
                            value={method.bank || ""}
                            onChange={(e) =>
                              updatePaymentMethod(index, "bank", e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder={t("russia_hands.form.bank_placeholder")}
                          />
                          <input
                            type="text"
                            value={method.card_number || ""}
                            onChange={(e) =>
                              updatePaymentMethod(index, "card_number", e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder={t("russia_hands.form.card_number_placeholder")}
                          />
                          <input
                            type="text"
                            value={method.type || ""}
                            onChange={(e) =>
                              updatePaymentMethod(index, "type", e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder={t("russia_hands.form.type_placeholder")}
                          />
                          <input
                            type="text"
                            value={method.comment || ""}
                            onChange={(e) =>
                              updatePaymentMethod(index, "comment", e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder={t("russia_hands.form.comment_placeholder")}
                          />
                        </div>
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => removePaymentMethod(index)}
                            className="text-sm text-red-600 hover:underline"
                          >
                            {t("russia_hands.form.remove_payment_method")}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t("russia_hands.form.sum_rub")}
                  </label>
                  <input
                    type="number"
                    value={formData.sum}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        sum: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    step="0.01"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t("russia_hands.form.sum_usdt")}
                  </label>
                  <input
                    type="number"
                    value={formData.sum_tether}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        sum_tether: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    step="0.01"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t("russia_hands.form.sum_total")}
                  </label>
                  <input
                    type="number"
                    value={formData.sum_total}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        sum_total: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.checked })
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    {t("russia_hands.form.status")}
                  </span>
                </label>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                {t("russia_hands.modal.cancel")}
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {createMutation.isPending || updateMutation.isPending
                  ? t("russia_hands.modal.saving")
                  : mode === "create"
                  ? t("russia_hands.modal.create")
                  : t("russia_hands.modal.update")}
              </button>
            </div>
          </form>
        </div>
      </div>

      <Notification
        message={notification.message}
        type={notification.type}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />
    </>
  );
};

const AdminRussiaHandsPage: React.FC = () => {
  const { t } = useTranslation("dashboard");
  const { showError, showSuccess, notification, hideNotification } =
    useNotification();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedHandler, setSelectedHandler] = useState<
    RussiaHand | undefined
  >();
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");

  const { data: handlers, isLoading, error } = useRussiaHands();

  const deleteMutation = useDeleteRussiaHand();

  const handleCreate = () => {
    setSelectedHandler(undefined);
    setModalMode("create");
    setIsModalOpen(true);
  };

  const handleEdit = (handler: RussiaHand) => {
    setSelectedHandler(handler);
    setModalMode("edit");
    setIsModalOpen(true);
  };

  const handleDelete = (handler: RussiaHand) => {
    if (window.confirm(t("russia_hands.delete_confirm"))) {
      deleteMutation.mutate(handler.alny_russia_hands_id, {
        onSuccess: (response: { message?: string }) => {
          showSuccess(response.message || t("russia_hands.success.deleted"));
        },
        onError: (error: any) => {
          const status = error?.response?.status;
          const responseData = error?.response?.data;

          let errorMessage = "";
          if (responseData) {
            if (responseData.message) {
              errorMessage = responseData.message;
            } else if (responseData.error) {
              errorMessage = responseData.error;
            }
          }

          if (!errorMessage) {
            errorMessage =
              error?.message || t("russia_hands.errors.deletion_error");
          }

          let userMessage = t("russia_hands.errors.deletion_error");

          switch (status) {
            case 400:
              userMessage = errorMessage;
              break;
            case 401:
              userMessage = t("russia_hands.errors.unauthorized");
              break;
            case 403:
              userMessage = t("russia_hands.errors.forbidden");
              break;
            case 404:
              userMessage = t("russia_hands.errors.not_found");
              break;
            case 500:
              userMessage = t("russia_hands.errors.server_error");
              break;
            default:
              userMessage = errorMessage;
          }

          showError(userMessage);
        },
      });
    }
  };

  const formatAmount = (amount: number) => {
    return amount.toLocaleString('ru-RU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).replace(',', '.');
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 dark:bg-gray-600 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 dark:bg-gray-600 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="text-red-500">
          {t("russia_hands.errors.loading_error")}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("russia_hands.title")}
          </h1>
          <button
            onClick={handleCreate}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {t("russia_hands.add_new")}
          </button>
        </div>

        {!handlers || handlers.data.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 text-4xl mb-4">👥</div>
            <p className="text-gray-500 dark:text-gray-400">{t("russia_hands.no_handlers")}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t("russia_hands.table.who")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t("russia_hands.table.sum_rub")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t("russia_hands.table.sum_total")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t("russia_hands.table.status")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t("russia_hands.table.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
                {handlers.data.map((handler) => (
                  <tr
                    key={handler.alny_russia_hands_id}
                    className="hover:bg-gray-50 dark:bg-gray-900"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {handler.alny_russia_hands_user_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {handler.who}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatAmount(handler.alny_russia_hands_sum)} ₽
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatAmount(handler.alny_russia_hands_sum_total)} ₽
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          handler.status
                            ? "text-green-600 bg-green-100"
                            : "text-red-600 bg-red-100"
                        }`}
                      >
                        {handler.status
                          ? t("russia_hands.status.active")
                          : t("russia_hands.status.inactive")}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(handler)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Редактировать"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L7.5 20.5H4v-3.5L16.732 3.732z"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(handler)}
                          disabled={deleteMutation.isPending}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50"
                          title="Удалить"
                        >
                          {deleteMutation.isPending ? (
                            <span className="text-xs">...</span>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2m-9 0h10"
                              />
                            </svg>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <RussiaHandModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        handler={selectedHandler}
        mode={modalMode}
      />

      <Notification
        message={notification.message}
        type={notification.type}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />
    </>
  );
};

export default AdminRussiaHandsPage;
